import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info);
  }

  handleReload = () => window.location.reload();

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="error-boundary">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle size={32} className="text-red-500" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-gray-800 dark:text-stone-200">
                कुछ गड़बड़ हो गई
              </h2>
              <p className="text-sm text-gray-500 dark:text-stone-400">
                {this.props.fallbackMessage || 'चिंता न करें, कृपया पेज को रीलोड करें या होम पर जाएं।'}
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReload}
                data-testid="error-reload-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-saffron text-white rounded-xl font-bold text-sm hover:bg-saffron-dark transition-colors"
              >
                <RefreshCw size={16} /> रीलोड करें
              </button>
              <button
                onClick={this.handleGoHome}
                data-testid="error-home-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 dark:bg-stone-700 text-gray-700 dark:text-stone-300 rounded-xl font-bold text-sm hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors"
              >
                <Home size={16} /> होम जाएं
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
